/**
 * useTenant — read the active tenant config from /api/tenant/config.
 * --------------------------------------------------------------
 * Returns the seeded `default` ForgedOps config when no X-Tenant-Id
 * is set OR when DB is unreachable, so the demo never goes blank.
 *
 * Caching strategy (iter 63 fix):
 *   1. Module-level `_tenantCache` — alive for the SPA session
 *   2. localStorage `forgeops.tenant.cache` — persists across reloads
 *      so subsequent navigations render the override immediately
 *      instead of flashing the default tenant until the fetch resolves
 *
 * Spinning up a white-label proof for a real prospect:
 *   localStorage.setItem('forgeops.tenant.id', 'acme-construction')
 *   window.location.reload()
 */
import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const DEFAULT_TENANT = {
  tenant_id:        "default",
  brand_name:       "FORGEDOPS",
  brand_name_short: "ForgedOps",
  owner_name:       "ForgedOps LLC",
  owner_website:    "https://forgedopshq.com",
  tagline_en:       "Building Safer Jobs · Powering Performance",
  tagline_es:       "Construyendo Trabajos Más Seguros · Impulsando el Desempeño",
  primary_color:    "#FF6A00",
  accent_color:     "#FFD000",
  warn_color:       "#FFB000",
  critical_color:   "#D32F2F",
  success_color:    "#2E7D32",
  panel_bg:         "#0A0A0A",
  logo_horizontal:  "/forgedops-horizontal.png",
  logo_vertical:    "/forgedops-vertical.png",
  logo_emblem:      "/forgedops-emblem.png",
  white_label:      false,
};

const CACHE_KEY      = "forgeops.tenant.cache";
const TENANT_ID_KEY  = "forgeops.tenant.id";

function _readCachedTenant() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.tenant_id) {
      return { ...DEFAULT_TENANT, ...parsed };
    }
  } catch { /* */ }
  return null;
}

function _publishCssVars(t) {
  try {
    const root = document.documentElement;
    if (t.primary_color) root.style.setProperty("--fo-primary-live", t.primary_color);
    if (t.panel_bg)      root.style.setProperty("--fo-panel-live",   t.panel_bg);
  } catch { /* */ }
}

let _tenantCache = _readCachedTenant();
if (_tenantCache) _publishCssVars(_tenantCache);
let _tenantPromise = null;

export function useTenant() {
  // Seed React state synchronously from whatever the SPA already
  // resolved (module cache → localStorage cache → DEFAULT) so the
  // first paint of every component matches the active tenant, even
  // before /api/tenant/config has a chance to respond on this load.
  const [tenant, setTenant] = useState(
    () => _tenantCache || _readCachedTenant() || DEFAULT_TENANT
  );

  useEffect(() => {
    if (!_tenantPromise) {
      const headers = { "Content-Type": "application/json" };
      try {
        const override = window.localStorage.getItem(TENANT_ID_KEY);
        if (override) headers["X-Tenant-Id"] = override;
      } catch { /* */ }
      _tenantPromise = fetch(`${API_BASE}/api/tenant/config`, { headers })
        .then((r) => (r.ok ? r.json() : DEFAULT_TENANT))
        .catch(() => DEFAULT_TENANT)
        .then((data) => {
          const merged = { ...DEFAULT_TENANT, ...data };
          _tenantCache = merged;
          try {
            window.localStorage.setItem(CACHE_KEY, JSON.stringify(merged));
          } catch { /* */ }
          _publishCssVars(merged);
          return merged;
        });
    }
    _tenantPromise.then(setTenant);
  }, []);

  return tenant;
}

export const tenantDefaults = DEFAULT_TENANT;
